import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  Show,
  type JSX,
} from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import {
  deviceTopic,
  type DashboardTile,
  type EnergyReport,
  type ReactiveReport,
  type ReactiveBucket,
  type EnergyResolution,
  type LatestValue,
  type PointReading,
  type PowerNow,
} from "@energyplatform/client";
import { api, live } from "../services.ts";
import { locale, t } from "../i18n.ts";
import {
  formatAgo,
  formatDateTime,
  formatNumber,
  formatRange,
  formatShare,
  formatTime,
} from "../format.ts";
import {
  pathTo,
  period,
  periodRange,
  range,
  scope,
  setScope,
  siteTree,
} from "../context.ts";
import { subtreeOf } from "../tree.ts";
import {
  averagePower,
  chartResolutions,
  completeness,
  defaultChartResolution,
  flowsNow,
  ALWAYS_SHOWN,
  batteryLow,
  byConditionOrder,
  conditionSummary,
  freshness,
  overviewGroup,
  peak,
  slots,
  type ConditionSummary,
  type Freshness,
  type OverviewGroup,
} from "../overview.ts";
import { dataState } from "../assets.ts";
import { access } from "../access.ts";
import { Icon, type IconName } from "../icons.tsx";
import { Chart, type ChartSeries } from "../components/Chart.tsx";
import { Sankey } from "../components/Sankey.tsx";
import { SitesMap } from "../components/SitesMap.tsx";
import { MarketPrices } from "../components/MarketPrices.tsx";
import { MediumBadge, mediumColor } from "../components/Media.tsx";
import { DataChip, ReadingValue, unitLabel } from "../components/PointCard.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import {
  DashboardView,
  EventsTile,
  HeadingTile,
  PointChartTile,
  PointValueTile,
} from "../components/Dashboard.tsx";
import {
  ConnectionStateChip,
  PageHeader,
  Skeleton,
} from "../components/ui.tsx";

const NOW_MIN_INTERVAL_MS = 5_000;
const BUCKET_MS: Record<string, number> = {
  "15m": 900_000,
  hour: 3_600_000,
  day: 86_400_000,
  month: 30 * 86_400_000,
};

/**
 * Overview of the scope node (prototype screen "Przegląd"): the grid connection now and today, the doc 06
 * indicators over the period, power drawn per bucket with its gaps, the balance now, the largest loads, what needs
 * attention and the organisation's sites.
 */
export function OverviewPage() {
  const nodeId = () => scope()?.id;
  const [now, { refetch: refetchNow }] = createResource(nodeId, (id) =>
    api.sites.now(id),
  );
  const [energy] = createResource(
    () => (nodeId() ? { id: nodeId()!, ...range() } : undefined),
    (q) => api.sites.energy(q.id, q.from, q.to, q.resolution),
  );
  const [reactive] = createResource(
    () => (nodeId() ? { id: nodeId()!, ...range() } : undefined),
    (q) => api.sites.reactive(q.id, q.from, q.to),
  );
  // Today's import and export for the grid band, whatever period is chosen.
  const [today] = createResource(
    () => (nodeId() ? { id: nodeId()!, period: period() } : undefined),
    (q) => {
      const r = periodRange("today");
      return api.sites.energy(q.id, r.from, r.to, r.resolution);
    },
  );

  const inScope = createMemo(() => {
    const tree = siteTree();
    const id = nodeId();
    if (!tree || !id) {
      return {
        nodes: new Set<string>(),
        points: [] as NonNullable<ReturnType<typeof siteTree>>["points"],
      };
    }
    const nodes = new Set(subtreeOf(tree.nodes, id));
    return { nodes, points: tree.points.filter((p) => nodes.has(p.nodeId)) };
  });

  // Power now follows the live hub: the host recalculates it (it owns the balance rules) when a device bound to a
  // point in the scope reports power, at most once per NOW_MIN_INTERVAL_MS.
  const devicesInScope = createMemo(
    () =>
      [
        ...new Set(
          inScope()
            .points.filter((p) => p.binding)
            .map((p) => p.binding!.deviceId),
        ),
      ].sort(),
    [],
    { equals: (a, b) => a.join() === b.join() },
  );
  createEffect(() => {
    const ids = new Set(devicesInScope());
    if (ids.size === 0) {
      return;
    }
    let last = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      timer = undefined;
      last = Date.now();
      if (nodeId()) {
        api.invalidate(`/api/v1/nodes/${encodeURIComponent(nodeId()!)}/now`);
        void refetchNow();
      }
    };
    const off = live.on<LatestValue[]>("device.readings", (changed) => {
      if (
        timer === undefined &&
        changed.some(
          (v) => ids.has(v.deviceId) && v.attribute.startsWith("power."),
        )
      ) {
        timer = setTimeout(
          refresh,
          Math.max(0, last + NOW_MIN_INTERVAL_MS - Date.now()),
        );
      }
    });
    const subs = [...ids].map((id) => live.subscribe(deviceTopic(id)));
    onCleanup(() => {
      off();
      clearTimeout(timer);
      for (const sub of subs) {
        void sub.then((unsubscribe) => unsubscribe());
      }
    });
  });

  const byGroup = createMemo(() => {
    const out: Record<OverviewGroup, ScopePoint[]> = {
      el: [],
      water: [],
      gas: [],
      air: [],
      env: [],
    };
    for (const p of inScope().points) {
      out[overviewGroup(p)].push(p);
    }
    return out;
  });
  const groups = (): OverviewGroup[] =>
    (["el", "water", "gas", "air", "env"] as const).filter(
      (g) => ALWAYS_SHOWN.includes(g) || byGroup()[g].length > 0,
    );
  // Values now of the points outside electricity (flows, volumes, conditions); electricity has its own balance now.
  const [readings] = createResource(
    () =>
      nodeId() && inScope().points.some((p) => overviewGroup(p) !== "el")
        ? nodeId()
        : undefined,
    async (id) =>
      new Map((await api.sites.pointsNow(id)).map((r) => [r.pointId, r])),
  );

  const crumbs = () =>
    pathTo(siteTree()?.nodes ?? [], nodeId()).map((n) => n.name);
  const mainPoints = () => byGroup().el.filter((p) => p.role === "main");
  const parentId = () => scope()?.parentId ?? null;

  /** The overview's own tiles, drawn with the page's data; the dashboard decides which and in what order. */
  const renderTile = (tile: DashboardTile): JSX.Element => {
    switch (tile.type) {
      case "heading":
        return (
          <HeadingTile
            tile={tile}
            group={(g) => (
              <GroupHead
                group={g as OverviewGroup}
                points={byGroup()[g as OverviewGroup]?.length ?? 0}
              />
            )}
          />
        );
      case "attention":
        return (
          <Attention
            points={inScope().points}
            now={now()}
            readings={readings()}
          />
        );
      case "gridBand":
        return (
          <Show when={!now.error} fallback={<ErrorBox error={now.error} />}>
            <Show
              when={now()}
              fallback={
                <section class="card">
                  <Skeleton />
                </section>
              }
            >
              {(n) => <GridBand now={n()} today={today()} />}
            </Show>
          </Show>
        );
      case "power":
        return (
          <Show
            when={!energy.error}
            fallback={<ErrorBox error={energy.error} />}
          >
            <Show
              when={energy()}
              fallback={
                <section class="card">
                  <Skeleton lines={6} />
                </section>
              }
            >
              {(e) => (
                <Show when={e().hasBoundary} fallback={<NoBoundary />}>
                  <PowerSection
                    report={e()}
                    now={now()}
                    nodeId={nodeId()!}
                    points={byGroup().el.length}
                  />
                </Show>
              )}
            </Show>
          </Show>
        );
      case "energyChart":
        return (
          <Show when={energy()?.hasBoundary}>
            <EnergyChart report={energy()!} />
          </Show>
        );
      case "balance":
        return (
          <Show when={energy()?.hasBoundary}>
            <Balance report={energy()!} />
          </Show>
        );
      case "reactive":
        return (
          <Show when={reactive()?.hasBoundary}>
            <Reactive report={reactive()!} />
          </Show>
        );
      case "loads":
        return <Show when={now()}>{(n) => <Loads now={n()} />}</Show>;
      case "market":
        return <MarketPrices />;
      case "water":
      case "gas":
      case "air": {
        const g = tile.type;
        return (
          <Show when={groups().includes(g)}>
            <GroupHead group={g} points={byGroup()[g].length} />
            <Utility
              group={g}
              nodeId={nodeId()}
              points={byGroup()[g]}
              readings={readings()}
            />
          </Show>
        );
      }
      case "conditions":
        return (
          <>
            <GroupHead group="env" points={byGroup().env.length} />
            <Conditions points={byGroup().env} readings={readings()} />
          </>
        );
      case "map":
        return <SitesMap />;
      case "pointChart":
        return <PointChartTile tile={tile} />;
      case "pointValue":
        return <PointValueTile tile={tile} />;
      case "events":
        return <EventsTile />;
      default:
        return null;
    }
  };

  return (
    <Show
      when={(siteTree()?.nodes.length ?? 0) > 0}
      fallback={
        <Show when={siteTree()} fallback={<Skeleton lines={6} />}>
          <PageHeader
            eyebrow={t("nav.monitoring")}
            title={t("overview.title")}
          />
          <section class="card empty">
            <h2>{t("overview.noTreeTitle")}</h2>
            <p class="muted">{t("overview.noTree")}</p>
            <div>
              <a class="btn primary" href="/assets">
                {t("overview.toAssets")}
              </a>
            </div>
          </section>
        </Show>
      }
    >
      <DashboardView
        header={(actions) => (
          <>
            <PageHeader
              eyebrow={`${[...crumbs().slice(0, -1), t("overview.title")].join(" / ")} · ${formatRange(range().from, range().to, locale())}`}
              title={scope()?.name ?? t("overview.title")}
              sub={`${
                mainPoints().length > 0
                  ? t("overview.boundaryOf", {
                      points: mainPoints()
                        .map((p) => p.name)
                        .join(", "),
                    })
                  : t("overview.noOwnBoundary")
              } · ${t("overview.pointCount", { count: inScope().points.length })}`}
            >
              <Show when={parentId()}>
                {(p) => (
                  <button
                    type="button"
                    class="btn"
                    onClick={() => setScope(p())}
                  >
                    <Icon name="chevl" />
                    {t("overview.levelUp")}
                  </button>
                )}
              </Show>
              <A class="btn" href="/assets">
                <Icon name="tree" />
                {t("nav.assets")}
              </A>
              {actions}
            </PageHeader>
          </>
        )}
        groups={groups().filter((g) => g === "el" || byGroup()[g].length > 0)}
        render={(tile) => renderTile(tile)}
      />
    </Show>
  );
}

/** The grid connection now and today (prototype "gridband"). */
function GridBand(props: { now: PowerNow; today: EnergyReport | undefined }) {
  const grid = () => props.now.grid;
  const exporting = () => grid() !== null && grid()! < -0.005;
  const day = () => props.today?.totals;
  return (
    <section class="card gridband">
      <Show
        when={props.now.hasBoundary}
        fallback={<span class="hint">{t("overview.noBoundaryNow")}</span>}
      >
        <div>
          <span class={`gridnow big ${exporting() ? "exp" : "imp"}`}>
            <Icon name={exporting() ? "leaf" : "bolt"} />
            <b class="num">
              {formatNumber(
                grid() === null ? null : Math.abs(grid()!),
                locale(),
                2,
              )}{" "}
              kW
            </b>
            <Icon name={exporting() ? "up" : "down"} />
            <span>
              {t(
                `overview.direction.${grid() === null ? "unknown" : exporting() ? "export" : "import"}`,
              )}
            </span>
          </span>
          <span class="hint">
            {props.now.at
              ? t("overview.measuredAt", {
                  time: formatTime(props.now.at, locale()),
                })
              : t("overview.noData")}
          </span>
        </div>
        <div>
          <span class="eyebrow">{t("overview.consumption")}</span>
          <b class="num">
            {formatNumber(props.now.consumption, locale(), 2)} kW
          </b>
        </div>
        <Show when={props.now.production !== null}>
          <div>
            <span class="eyebrow">{t("overview.production")}</span>
            <b class="num" style={{ color: "var(--m-pv)" }}>
              {formatNumber(props.now.production, locale(), 2)} kW
            </b>
          </div>
        </Show>
        <div>
          <span class="eyebrow">{t("overview.todayGrid")}</span>
          <span class="gday">
            <span class="imp">
              <Icon name="down" />
              {t("overview.imported", {
                value: formatNumber(day()?.import ?? null, locale(), 1),
              })}
            </span>
            <span class="exp">
              <Icon name="up" />
              {t("overview.exported", {
                value: formatNumber(day()?.export ?? null, locale(), 1),
              })}
            </span>
          </span>
        </div>
        <Show when={day()?.ecoLevel}>
          {(level) => (
            <div>
              <span class="eyebrow">{t("overview.ecoToday")}</span>
              <span
                class={`ecob ${level() === "eco" ? "eco" : level() === "partial" ? "part" : ""}`}
              >
                <Icon name="leaf" />
                {t(`eco.${level()}`)} ·{" "}
                {formatShare(day()!.selfSufficiency, locale())}
              </span>
            </div>
          )}
        </Show>
      </Show>
    </section>
  );
}

function NoBoundary() {
  return (
    <section class="card">
      <div class="note">
        <Icon name="alert" />
        <span>{t("overview.noBoundary")}</span>
        <a class="btn sm" href="/assets" style={{ "margin-left": "auto" }}>
          {t("overview.toAssets")}
        </a>
      </div>
    </section>
  );
}

/** KPIs, the power chart and the balance now; power comes from the balance's energy per bucket. */
function PowerSection(props: {
  report: EnergyReport;
  now: PowerNow | undefined;
  nodeId: string;
  points: number;
}) {
  const navigate = useNavigate();
  const [res, setRes] = createSignal<EnergyResolution>(
    defaultChartResolution(range().from, range().to),
  );
  createEffect(() => setRes(defaultChartResolution(range().from, range().to)));
  const [fine] = createResource(
    () => ({ id: props.nodeId, ...range(), res: res() }),
    (q) => api.sites.energy(q.id, q.from, q.to, q.res),
  );
  const timeline = createMemo(() =>
    fine()
      ? slots(fine()!.buckets, range().from, range().to, new Date(), res())
      : [],
  );
  const quality = createMemo(() => completeness(timeline()));
  const consumption = createMemo(() =>
    averagePower(timeline(), (b) => b.consumption),
  );
  const top = createMemo(() => peak(consumption()));
  // Reactive power at the connection, inductive and capacitive, as the mean kvar of each finished bucket.
  const [reactive] = createResource(
    () => ({ id: props.nodeId, ...range(), res: res() }),
    (q) => api.sites.reactiveSeries(q.id, q.from, q.to, q.res).catch(() => []),
  );
  const reactiveLine = (pick: (b: ReactiveBucket) => number | null) => {
    const now = Date.now();
    return (reactive() ?? []).map((b) => {
      const start = new Date(b.at);
      const hours =
        res() === "15m"
          ? 0.25
          : res() === "hour"
            ? 1
            : res() === "day"
              ? 24
              : new Date(
                  start.getFullYear(),
                  start.getMonth() + 1,
                  0,
                ).getDate() * 24;
      const v = pick(b);
      return {
        t: start.getTime(),
        v:
          v === null || start.getTime() + hours * 3_600_000 > now
            ? null
            : v / hours,
      };
    });
  };
  const hasProduction = () => props.report.totals.production !== null;
  const series = createMemo<ChartSeries[]>(() => {
    const out: ChartSeries[] = [
      {
        key: "use",
        label: t("overview.consumption"),
        color: "var(--m-el)",
        kind: "area",
        points: consumption(),
      },
    ];
    if (hasProduction()) {
      out.push({
        key: "pv",
        label: t("overview.production"),
        color: "var(--m-pv)",
        kind: "line",
        dashed: true,
        points: averagePower(timeline(), (b) => b.production),
      });
      out.push({
        key: "grid",
        label: t("overview.kpi.import"),
        color: "var(--s2)",
        kind: "line",
        points: averagePower(timeline(), (b) => b.import),
      });
    }
    if ((reactive() ?? []).some((b) => b.inductive !== null)) {
      out.push({
        key: "inductive",
        label: t("overview.reactiveInductive"),
        color: "var(--crit)",
        kind: "line",
        dashed: true,
        points: reactiveLine((b) => b.inductive),
      });
    }
    if ((reactive() ?? []).some((b) => b.capacitive)) {
      out.push({
        key: "capacitive",
        label: t("overview.reactiveCapacitive"),
        color: "var(--s4)",
        kind: "line",
        dashed: true,
        points: reactiveLine((b) => b.capacitive),
      });
    }
    return out;
  });
  const flows = createMemo(() => {
    const n = props.now;
    const tree = siteTree();
    if (!n || !tree) {
      return null;
    }
    return flowsNow(n, {
      grid: t("overview.grid"),
      pv: t("overview.production"),
      export: t("overview.toGrid"),
      rest: t("overview.unmetered"),
      unmeasured: t("overview.unmeasured"),
    });
  });
  const tot = () => props.report.totals;

  return (
    <>
      <div class="grid g-kpi">
        <section class="card kpi">
          <span class="lbl">
            <i class="dot" style={{ background: "var(--m-el)" }} />
            {t("overview.now")}
          </span>
          <span class="val">
            {formatNumber(props.now?.consumption ?? null, locale(), 2)}
            <small>kW</small>
          </span>
          <span class="q">
            <span class="chip ok">{t("overview.boundaryBalance")}</span>
            {props.now?.at ? formatTime(props.now.at, locale()) : ""}
          </span>
        </section>
        <section class="card kpi">
          <span class="lbl">
            <i class="dot" style={{ background: "var(--m-el)" }} />
            {t("overview.kpi.energy")}
          </span>
          <span class="val">
            {formatNumber(tot().consumption, locale(), 1)}
            <small>kWh</small>
          </span>
          <span class="q">
            <Show when={quality().share !== null}>
              <span class={`chip ${quality().share! >= 0.995 ? "ok" : "warn"}`}>
                {t("overview.completeness", {
                  share: formatShare(quality().share, locale()),
                })}
              </span>
              <Show when={quality().gapHours > 0}>
                {t("overview.gapHours", {
                  hours: formatNumber(
                    quality().gapHours,
                    locale(),
                    quality().gapHours < 10 ? 1 : 0,
                  ),
                })}
              </Show>
            </Show>
          </span>
        </section>
        <section class="card kpi">
          <span class="lbl">
            <i class="dot" style={{ background: "var(--m-el)" }} />
            {t("overview.kpi.peak")}
          </span>
          <span class="val">
            {formatNumber(top()?.v ?? null, locale(), 2)}
            <small>kW</small>
          </span>
          <span class="q">
            <Show when={top()}>
              {(p) =>
                res() === "15m" || res() === "hour"
                  ? `${formatDateTime(new Date(p().t), locale())}–${formatTime(new Date(p().to ?? p().t), locale())}`
                  : formatDateTime(new Date(p().t), locale())
              }
            </Show>
            <span class="chip">{t(`overview.peakOf.${res()}`)}</span>
          </span>
        </section>
        <section class="card kpi">
          <span class="lbl">{t("overview.kpi.gridExchange")}</span>
          <span class="val">
            {formatNumber(tot().import, locale(), 1)}
            <small>kWh</small>
          </span>
          <span class="q">
            {t("overview.exported", {
              value: formatNumber(tot().export, locale(), 1),
            })}
          </span>
        </section>
        <section class="card kpi">
          <span class="lbl">{t("overview.kpi.selfSufficiency")}</span>
          <span class="val">
            {formatShare(tot().selfSufficiency, locale())}
          </span>
          <Show when={tot().ecoLevel}>
            {(level) => (
              <span
                class={`ecob ${level() === "eco" ? "eco" : level() === "partial" ? "part" : ""}`}
              >
                <Icon name="leaf" />
                {t(`eco.${level()}`)}
              </span>
            )}
          </Show>
          <Show when={tot().selfConsumption !== null}>
            <span class="q">
              {t("overview.selfConsumptionOf", {
                share: formatShare(tot().selfConsumption, locale()),
              })}
            </span>
          </Show>
        </section>
        <section
          class="card kpi click"
          role="button"
          tabindex="0"
          onClick={() => navigate("/assets")}
        >
          <span class="lbl">{t("overview.kpi.points")}</span>
          <span class="val">{props.points}</span>
          <span class="q">{t("overview.pointsHint")}</span>
        </section>
      </div>
      <div class="grid g-2">
        <section class="card">
          <div class="card-h">
            <h2>{t("overview.powerChart", { name: scope()?.name ?? "" })}</h2>
            <span class="meta">
              kW · {t(`overview.resolutionAvg.${res()}`)}
            </span>
          </div>
          <div class="note" style={{ "flex-wrap": "wrap", gap: "10px" }}>
            <div class="seg" role="group" aria-label={t("overview.resolution")}>
              <For each={chartResolutions(range().from, range().to)}>
                {(r) => (
                  <button
                    type="button"
                    aria-pressed={res() === r}
                    onClick={() => setRes(r)}
                  >
                    {t(`overview.resolutionShort.${r}`)}
                  </button>
                )}
              </For>
            </div>
            <span class="hint" style={{ "margin-left": "auto" }}>
              {t("overview.powerHint")}
            </span>
          </div>
          <div class="card-b">
            <Show when={!fine.error} fallback={<ErrorBox error={fine.error} />}>
              <Show when={fine()} fallback={<Skeleton lines={5} />}>
                <Chart
                  series={series()}
                  from={range().from}
                  to={range().to}
                  unit="kW"
                  locale={locale()}
                  bucketMs={BUCKET_MS[res()]}
                  emptyText={t("overview.noEnergy")}
                  gaps={quality().gaps}
                  gapLabel={t("overview.gap")}
                  now={Date.now()}
                  nowLabel={t("overview.nowMark")}
                />
              </Show>
            </Show>
          </div>
        </section>
        <section class="card">
          <div class="card-h">
            <h2>{t("overview.balanceNow")}</h2>
            <span class="meta">{t("overview.balanceNowMeta")}</span>
          </div>
          <Show
            when={flows()}
            fallback={<div class="card-b hint">{t("overview.noData")}</div>}
          >
            {(f) => (
              <>
                <div class="card-b" style={{ "padding-top": "6px" }}>
                  <Sankey
                    flows={f()}
                    center={scope()?.name ?? ""}
                    unit="kW"
                    locale={locale()}
                    label={t("overview.balanceNow")}
                    restNote={t("overview.calculated")}
                    onPick={(id) =>
                      navigate(`/assets?point=${encodeURIComponent(id)}`)
                    }
                  />
                </div>
                <div class="legend">
                  <span>{t("overview.unmeteredHint")}</span>
                </div>
              </>
            )}
          </Show>
        </section>
      </div>
    </>
  );
}

function EnergyChart(props: { report: EnergyReport }) {
  const series = createMemo<ChartSeries[]>(() => {
    const at = (iso: string) => new Date(iso).getTime();
    const b = props.report.buckets;
    const out: ChartSeries[] = [
      {
        key: "import",
        label: t("overview.kpi.import"),
        color: "var(--s2)",
        kind: "bar",
        points: b.map((x) => ({ t: at(x.at), v: x.import })),
      },
      {
        key: "export",
        label: t("overview.kpi.export"),
        color: "var(--ok)",
        kind: "bar",
        points: b.map((x) => ({
          t: at(x.at),
          v: x.export === null ? null : -x.export,
        })),
      },
    ];
    if (props.report.totals.production !== null) {
      out.push({
        key: "production",
        label: t("overview.kpi.production"),
        color: "var(--m-pv)",
        kind: "line",
        dashed: true,
        points: b.map((x) => ({ t: at(x.at), v: x.production })),
      });
    }
    return out;
  });
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("overview.energyChart")}</h2>
        <span class="meta">kWh · {t(`resolution.${range().resolution}`)}</span>
      </div>
      <div class="card-b">
        <Chart
          series={series()}
          from={range().from}
          to={range().to}
          unit="kWh"
          locale={locale()}
          bucketMs={BUCKET_MS[range().resolution]}
          emptyText={t("overview.noEnergy")}
          height={200}
        />
        <p class="hint" style={{ margin: "8px 0 0" }}>
          {t("overview.energyHint")}
        </p>
      </div>
    </section>
  );
}

/** Where the consumed energy came from: the grid or own production used on site. */
function Balance(props: { report: EnergyReport }) {
  const tot = () => props.report.totals;
  const own = () =>
    tot().production === null || tot().export === null
      ? null
      : Math.max(0, tot().production! - tot().export!);
  const total = () => tot().consumption ?? 0;
  const share = (v: number | null) =>
    v === null || total() <= 0 ? 0 : Math.min(100, (v / total()) * 100);
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("overview.balance")}</h2>
      </div>
      <div class="card-b bars">
        <div class="b">
          <span>{t("overview.fromGrid")}</span>
          <span class="num">{formatNumber(tot().import, locale(), 1)} kWh</span>
          <div class="t">
            <i
              style={{
                width: `${share(tot().import)}%`,
                background: "var(--s2)",
              }}
            />
          </div>
        </div>
        <Show when={own() !== null}>
          <div class="b">
            <span>{t("overview.fromOwn")}</span>
            <span class="num">{formatNumber(own(), locale(), 1)} kWh</span>
            <div class="t">
              <i
                style={{ width: `${share(own())}%`, background: "var(--m-pv)" }}
              />
            </div>
          </div>
        </Show>
        <div class="b">
          <span>{t("overview.toGrid")}</span>
          <span class="num">{formatNumber(tot().export, locale(), 1)} kWh</span>
          <div class="t">
            <i
              style={{
                width: `${share(tot().export)}%`,
                background: "var(--ok)",
              }}
            />
          </div>
        </div>
        <p class="hint" style={{ margin: 0 }}>
          {t("overview.balanceHint")}
        </p>
      </div>
    </section>
  );
}

/**
 * Reactive energy at the grid connection (doc 06): tan φ and the share of reactive in active energy, inductive and
 * capacitive kvarh, and the inductive part above the operator's threshold, which companies pay for.
 */
function Reactive(props: { report: ReactiveReport }) {
  const r = () => props.report;
  const over = () => r().tanPhi !== null && r().tanPhi! > r().threshold;
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("overview.reactive.title")}</h2>
        <Show when={r().source}>
          <span class="meta">
            {t(`overview.reactive.source.${r().source}`)}
          </span>
        </Show>
      </div>
      <Show
        when={r().source}
        fallback={
          <div class="card-b">
            <p class="muted" style={{ margin: 0 }}>
              {t("overview.reactive.none")}
            </p>
          </div>
        }
      >
        <div class="card-b" style={{ display: "grid", gap: "10px" }}>
          <div
            style={{
              display: "flex",
              gap: "10px",
              "align-items": "baseline",
              "flex-wrap": "wrap",
            }}
          >
            <span
              class="num"
              style={{ "font-size": "26px", "font-weight": 500 }}
            >
              tg φ {formatNumber(r().tanPhi, locale(), 2)}
            </span>
            <Show when={r().tanPhi !== null}>
              <span class={`chip ${over() ? "warn" : "ok"}`}>
                {over()
                  ? t("overview.reactive.over", {
                      threshold: formatNumber(r().threshold, locale(), 1),
                    })
                  : t("overview.reactive.within", {
                      threshold: formatNumber(r().threshold, locale(), 1),
                    })}
              </span>
            </Show>
          </div>
          <dl class="kv" style={{ margin: 0 }}>
            <dt>{t("overview.reactive.inductive")}</dt>
            <dd class="num">
              {formatNumber(r().inductive, locale(), 1)} kvarh
              <Show when={r().tanPhi !== null}>
                {" "}
                ·{" "}
                {t("overview.reactive.share", {
                  share: formatShare(r().tanPhi, locale()),
                })}
              </Show>
            </dd>
            <dt>{t("overview.reactive.capacitive")}</dt>
            <dd class="num">
              {formatNumber(r().capacitive, locale(), 1)} kvarh
            </dd>
            <dt>{t("overview.reactive.active")}</dt>
            <dd class="num">
              {formatNumber(r().activeImport, locale(), 1)} kWh
            </dd>
            <dt>{t("overview.reactive.excess")}</dt>
            <dd
              class="num"
              style={{
                color: (r().excess ?? 0) > 0 ? "var(--warn)" : undefined,
              }}
            >
              {formatNumber(r().excess, locale(), 1)} kvarh
            </dd>
          </dl>
          <Show when={r().source === "estimate" && r().completeness !== null}>
            <p class="hint" style={{ margin: 0 }}>
              {t("overview.reactive.completeness", {
                share: formatShare(r().completeness, locale()),
              })}
            </p>
          </Show>
          <p class="hint" style={{ margin: 0 }}>
            {t("overview.reactive.hint")}
          </p>
        </div>
      </Show>
    </section>
  );
}

function FreshChip(props: {
  state: Freshness;
  at: string | null;
  power: number | null;
}) {
  return (
    <Show
      when={props.state === "ok"}
      fallback={
        <span class={`chip ${props.state === "late" ? "warn" : "crit"}`}>
          {props.state === "late"
            ? t("overview.lateSince", { ago: formatAgo(props.at, locale()) })
            : t("overview.noReading")}
        </span>
      }
    >
      <span class={`chip ${props.power === 0 ? "" : "ok"}`}>
        {props.power === 0 ? t("overview.measuredZero") : t("overview.current")}
      </span>
    </Show>
  );
}

/** Points below the grid connection by power now; they never add up to the balance (doc 12, section 7). */
function Loads(props: { now: PowerNow }) {
  const navigate = useNavigate();
  const [all, setAll] = createSignal(false);
  const loads = () =>
    props.now.points
      .filter(
        (p) =>
          p.role !== "main" && p.role !== "source" && p.role !== "environment",
      )
      .sort((a, b) => Math.abs(b.power ?? 0) - Math.abs(a.power ?? 0));
  const max = () =>
    Math.max(0.001, ...loads().map((p) => Math.abs(p.power ?? 0)));
  const nodeName = (pointId: string) => {
    const tree = siteTree();
    const nodeId = tree?.points.find((p) => p.id === pointId)?.nodeId;
    return tree?.nodes.find((n) => n.id === nodeId)?.name ?? "";
  };
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("overview.loads")}</h2>
        <span class="meta">{t("overview.loadsHint")}</span>
      </div>
      <Show
        when={loads().length > 0}
        fallback={<div class="card-b hint">{t("overview.noLoads")}</div>}
      >
        <div class="scrollx">
          <table class="tbl">
            <tbody>
              <For each={all() ? loads() : loads().slice(0, 8)}>
                {(p) => {
                  const state = () => freshness(p.at, Date.now());
                  return (
                    <tr
                      class="click"
                      onClick={() =>
                        navigate(
                          `/assets?point=${encodeURIComponent(p.pointId)}`,
                        )
                      }
                    >
                      <td style={{ width: "24px" }}>
                        <i
                          class="dot"
                          style={{ background: mediumColor(p.medium) }}
                        />
                      </td>
                      <td>
                        {p.name}
                        <div class="hint">{nodeName(p.pointId)}</div>
                      </td>
                      <td style={{ width: "32%" }}>
                        <div
                          style={{
                            height: "6px",
                            "border-radius": "3px",
                            background: "var(--sunken)",
                          }}
                        >
                          <div
                            style={{
                              height: "6px",
                              "border-radius": "3px",
                              width: `${(Math.abs(p.power ?? 0) / max()) * 100}%`,
                              background:
                                state() === "ok"
                                  ? mediumColor(p.medium)
                                  : "var(--stale)",
                            }}
                          />
                        </div>
                      </td>
                      <td class="r num">
                        {formatNumber(p.power, locale(), 2)} kW
                      </td>
                      <td>
                        <FreshChip state={state()} at={p.at} power={p.power} />
                      </td>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
        </div>
        <Show when={loads().length > 8}>
          <div class="card-b">
            <button
              type="button"
              class="btn ghost sm"
              onClick={() => setAll(!all())}
            >
              {all()
                ? t("common.showLess")
                : t("common.showAll", { count: loads().length })}
            </button>
          </div>
        </Show>
      </Show>
    </section>
  );
}

interface AttentionItem {
  key: string;
  icon: IconName;
  title: string;
  detail: string;
  href: string;
  level: "warn" | "crit";
}

/**
 * What needs attention in the scope, from facts the platform already has: integrations that are not running,
 * points without current data and points without a device.
 */
function Attention(props: {
  points: NonNullable<ReturnType<typeof siteTree>>["points"];
  now: PowerNow | undefined;
  readings: ReadonlyMap<string, PointReading> | undefined;
}) {
  // Integrations belong to the whole organisation: people with access to branches only do not see them.
  const [connections] = createResource(
    () => access().unrestricted || undefined,
    () => api.connections.list(),
  );
  const items = createMemo<AttentionItem[]>(() => {
    const out: AttentionItem[] = [];
    for (const c of connections() ?? []) {
      if (c.status.state !== "active") {
        out.push({
          key: `c:${c.id}`,
          icon: "plug",
          title: t("overview.attention.integration", { name: c.name }),
          detail:
            c.status.lastErrorCode ?? t(`connectionState.${c.status.state}`),
          href: `/integrations/${encodeURIComponent(c.id)}`,
          level:
            c.status.state === "error" || c.status.state === "authExpired"
              ? "crit"
              : "warn",
        });
      }
    }
    const powers = new Map(
      (props.now?.points ?? []).map((p) => [p.pointId, p]),
    );
    for (const p of props.points) {
      if (!p.binding) {
        out.push({
          key: `u:${p.id}`,
          icon: "link",
          title: t("overview.attention.unbound", { name: p.name }),
          detail: t("overview.attention.unboundHint"),
          href: `/assets?point=${encodeURIComponent(p.id)}`,
          level: "warn",
        });
        continue;
      }
      const power = powers.get(p.id);
      if (power) {
        const state = freshness(power.at, Date.now());
        if (state !== "ok") {
          out.push({
            key: `s:${p.id}`,
            icon: "clock",
            title: t("overview.attention.stale", { name: p.name }),
            detail:
              state === "late"
                ? t("overview.lateSince", {
                    ago: formatAgo(power.at, locale()),
                  })
                : t("overview.noReading"),
            href: `/assets?point=${encodeURIComponent(p.id)}`,
            level: p.role === "main" ? "crit" : "warn",
          });
        }
      }
    }
    // Devices whose battery needs replacing, before they stop sending data.
    for (const p of props.points) {
      const r = props.readings?.get(p.id);
      if (r && batteryLow(r)) {
        out.push({
          key: `b:${p.id}`,
          icon: "battery",
          title: t("overview.attention.battery", { name: p.name }),
          detail:
            r.batteryState === "empty"
              ? t("overview.battery.empty")
              : t("overview.battery.low", {
                  level:
                    r.batteryLevel != null
                      ? `${formatNumber(r.batteryLevel, locale(), 0)} %`
                      : "",
                }),
          href: `/assets?point=${encodeURIComponent(p.id)}`,
          level: r.batteryState === "empty" ? "crit" : "warn",
        });
      }
    }
    return out.sort((a, b) =>
      a.level === b.level ? 0 : a.level === "crit" ? -1 : 1,
    );
  });
  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("overview.attention.title")}</h2>
        <span class="meta">{t("overview.attention.meta")}</span>
      </div>
      <Show
        when={items().length > 0}
        fallback={<div class="card-b hint">{t("overview.attention.none")}</div>}
      >
        <div>
          <For each={items()}>
            {(i) => (
              <A class="evmini" href={i.href}>
                <span
                  style={{
                    color: i.level === "crit" ? "var(--crit)" : "var(--warn)",
                  }}
                >
                  <Icon name={i.icon} />
                </span>
                <span style={{ flex: 1, "min-width": 0 }}>
                  <b style={{ "font-weight": 500 }}>{i.title}</b>
                  <span class="hint" style={{ display: "block" }}>
                    {i.detail}
                  </span>
                </span>
                <Show when={i.key.startsWith("c:")}>
                  <ConnectionStateChip
                    state={
                      (connections() ?? []).find((c) => `c:${c.id}` === i.key)!
                        .status.state
                    }
                  />
                </Show>
              </A>
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

type ScopePoint = NonNullable<ReturnType<typeof siteTree>>["points"][number];

/** Badge of a section: its medium, and a thermometer for conditions. */
const GROUP_MEDIUM: Record<OverviewGroup, string> = {
  el: "el",
  water: "water",
  gas: "gas",
  air: "air",
  env: "temp",
};

const sectionId = (g: OverviewGroup) => `ov-${g}`;

function GroupHead(props: { group: OverviewGroup; points: number }) {
  return (
    <div class="sect-h ovsec" id={sectionId(props.group)}>
      <MediumBadge medium={GROUP_MEDIUM[props.group]} />
      <h2>{t(`overview.group.${props.group}`)}</h2>
      <span class="cnt">
        {t("overview.pointCount", { count: props.points })}
      </span>
    </div>
  );
}

const ROLE_ORDER = ["main", "source", "storage", "sub", "load"];

/**
 * Water, gas or compressed air in the scope: the meters with their value now and their volume over the period
 * (connections first), the balance of the connections against the sub-meters they feed, and the cost from the
 * medium's billing points.
 */
function Utility(props: {
  group: "water" | "gas" | "air";
  nodeId: string | undefined;
  points: ScopePoint[];
  readings: ReadonlyMap<string, PointReading> | undefined;
}) {
  const navigate = useNavigate();
  const nodeName = (id: string) =>
    siteTree()?.nodes.find((n) => n.id === id)?.name ?? "";
  const sorted = () =>
    [...props.points].sort(
      (a, b) =>
        ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) ||
        a.name.localeCompare(b.name),
    );
  const current = () =>
    props.points.filter(
      (p) => dataState(p, props.readings?.get(p.id), Date.now()) === "ok",
    ).length;
  const medium = () => t(`medium.${props.group}`);
  const [report] = createResource(
    () =>
      props.nodeId && props.points.length > 0
        ? { id: props.nodeId, group: props.group, ...range() }
        : undefined,
    (q) => api.sites.utility(q.id, q.group, q.from, q.to, q.resolution),
  );
  const volumeOf = (id: string) =>
    report()?.points.find((x) => x.pointId === id);
  const m3 = (v: number | null | undefined) =>
    v === null || v === undefined
      ? "—"
      : `${formatNumber(v, locale(), Math.abs(v) < 10 ? 2 : Math.abs(v) < 100 ? 1 : 0)} m³`;
  const missing = () => report()?.points.filter((x) => x.missingWeight) ?? [];
  return (
    <Show
      when={props.points.length > 0}
      fallback={
        <section class="card">
          <div class="card-b ovempty">
            <span class="hint">
              {t("overview.group.empty", { medium: medium() })}
            </span>
            <A class="btn" href="/assets">
              <Icon name="plus" />
              {t("overview.group.addMeter")}
            </A>
          </div>
        </section>
      }
    >
      <div class="grid g-2">
        <section class="card">
          <div class="card-h">
            <h2>{t("overview.group.meters")}</h2>
            <span class="meta">
              {t("overview.group.current", {
                current: current(),
                total: props.points.length,
              })}
            </span>
          </div>
          <div class="scrollx">
            <table class="tbl">
              <thead>
                <tr>
                  <th colspan="2">{t("overview.group.meter")}</th>
                  <th class="r">{t("overview.group.now")}</th>
                  <th class="r">{t("overview.group.inPeriod")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <For each={sorted()}>
                  {(p) => (
                    <tr
                      class="click"
                      onClick={() =>
                        navigate(`/assets?point=${encodeURIComponent(p.id)}`)
                      }
                    >
                      <td style={{ width: "24px" }}>
                        <MediumBadge medium={p.medium} />
                      </td>
                      <td>
                        {p.name}
                        <div class="hint">
                          {t(`role.point.${p.role}`)} · {nodeName(p.nodeId)}
                        </div>
                      </td>
                      <td class="r num">
                        <ReadingValue reading={props.readings?.get(p.id)} />
                      </td>
                      <td class="r num">
                        <Show
                          when={!volumeOf(p.id)?.missingWeight}
                          fallback={
                            <span
                              class="chip warn"
                              title={t("overview.group.noWeightHint")}
                            >
                              {t("overview.group.noWeight")}
                            </span>
                          }
                        >
                          {report.loading ? "…" : m3(volumeOf(p.id)?.volume)}
                        </Show>
                      </td>
                      <td>
                        <DataChip
                          point={p}
                          reading={props.readings?.get(p.id)}
                        />
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </section>
        <div style={{ display: "grid", gap: "16px", "align-content": "start" }}>
          <section class="card">
            <div class="card-h">
              <h2>{t("overview.group.period", { medium: medium() })}</h2>
              <span class="meta">
                m³ · {formatRange(range().from, range().to, locale())}
              </span>
            </div>
            <div class="card-b">
              <Show
                when={!report.error}
                fallback={<ErrorBox error={report.error} />}
              >
                <Show when={report()} fallback={<Skeleton lines={3} />}>
                  {(r) => (
                    <Show
                      when={r().hasBoundary}
                      fallback={
                        <p class="hint" style={{ margin: 0 }}>
                          {t("overview.group.noConnection", {
                            medium: medium(),
                          })}
                        </p>
                      }
                    >
                      <dl class="kv">
                        <dt>{t("overview.group.input")}</dt>
                        <dd class="num">{m3(r().input)}</dd>
                        <dt>{t("overview.group.metered")}</dt>
                        <dd class="num">
                          {r().outlets.length === 0 ? (
                            <span class="hint">
                              {t("overview.group.noSubmeters")}
                            </span>
                          ) : (
                            m3(r().metered)
                          )}
                        </dd>
                        <dt>{t("overview.group.unmetered")}</dt>
                        <dd class="num">
                          {m3(r().unmetered)}
                          <Show when={r().unmeteredShare !== null}>
                            {" "}
                            <span class="hint">
                              ({formatShare(r().unmeteredShare, locale())})
                            </span>
                          </Show>
                        </dd>
                        <dt>{t("flows.coverage")}</dt>
                        <dd>
                          {t("flows.coverageText", {
                            complete: r().completeBuckets,
                            total: r().buckets.length,
                          })}
                        </dd>
                      </dl>
                    </Show>
                  )}
                </Show>
              </Show>
              <Show when={missing().length > 0}>
                <p class="hint" style={{ "margin-bottom": 0 }}>
                  {t("overview.group.noWeightList", {
                    names: missing()
                      .map((x) => x.name)
                      .join(", "),
                  })}
                </p>
              </Show>
            </div>
          </section>
          <UtilityCosts
            group={props.group}
            nodeId={props.nodeId}
            medium={medium()}
          />
        </div>
      </div>
    </Show>
  );
}

/**
 * Cost of water or gas over the period: the billing points of the medium under the scope, estimated from metered
 * volumes and their terms (an invoice for exactly the period replaces the estimate), by category.
 */
function UtilityCosts(props: {
  group: "water" | "gas" | "air";
  nodeId: string | undefined;
  medium: string;
}) {
  const [costs] = createResource(
    () =>
      props.nodeId && props.group !== "air"
        ? { id: props.nodeId, ...range() }
        : undefined,
    (q) => api.finance.costs(q.id, q.from, q.to),
  );
  const points = () =>
    (costs()?.points ?? []).filter((p) => p.medium === props.group);
  const total = () =>
    points().reduce((a, p) => a + (p.invoice?.net ?? p.estimate.total), 0);
  const byCategory = () => {
    const out = new Map<string, number>();
    for (const p of points()) {
      for (const [k, v] of Object.entries(p.estimate.byCategory)) {
        if (v) {
          out.set(k, (out.get(k) ?? 0) + v);
        }
      }
    }
    return [...out.entries()];
  };
  const zl = (v: number) => `${formatNumber(v, locale(), 2)} zł`;
  // Compressed air is not billed: it is made on site from electricity.
  return (
    <Show when={props.group !== "air"}>
      <section class="card">
        <div class="card-h">
          <h2>{t("overview.group.costs", { medium: props.medium })}</h2>
          <A class="meta" href="/finance">
            {t("nav.finance")}
          </A>
        </div>
        <div class="card-b">
          <Show when={!costs.error} fallback={<ErrorBox error={costs.error} />}>
            <Show when={costs()} fallback={<Skeleton lines={2} />}>
              <Show
                when={points().length > 0}
                fallback={
                  <p class="hint" style={{ margin: 0 }}>
                    {t("overview.group.noBilling", { medium: props.medium })}
                  </p>
                }
              >
                <dl class="kv">
                  <dt>{t("finance.costs.total")}</dt>
                  <dd class="num">
                    <b>{zl(total())}</b>
                  </dd>
                  <For each={byCategory()}>
                    {([k, v]) => (
                      <>
                        <dt>{t(`finance.category.${k}`)}</dt>
                        <dd class="num">{zl(v)}</dd>
                      </>
                    )}
                  </For>
                  <dt>{t("finance.costs.completeness")}</dt>
                  <dd>
                    {formatShare(
                      points().reduce(
                        (a, p) => a + p.estimate.completeness,
                        0,
                      ) / points().length,
                      locale(),
                    )}
                  </dd>
                </dl>
                <p class="hint" style={{ "margin-bottom": 0 }}>
                  {points().some((p) => p.invoice)
                    ? t("overview.group.costsInvoice")
                    : t("finance.costs.estimateNote")}
                </p>
              </Show>
            </Show>
          </Show>
        </div>
      </section>
    </Show>
  );
}

/**
 * Conditions in the scope (temperature, humidity, light, pressure…): a summary per quantity, then every sensor with
 * its value now. They are never part of the balance, so they are listed apart from loads.
 */
function Conditions(props: {
  points: ScopePoint[];
  readings: ReadonlyMap<string, PointReading> | undefined;
}) {
  const navigate = useNavigate();
  const nodeName = (id: string) =>
    siteTree()?.nodes.find((n) => n.id === id)?.name ?? "";
  const summary = createMemo(() =>
    conditionSummary(props.points, props.readings ?? new Map(), Date.now()),
  );
  const value = (s: ConditionSummary, v: number | null) =>
    v === null
      ? "—"
      : `${formatNumber(v, locale(), Math.abs(v) < 10 ? 2 : Math.abs(v) < 100 ? 1 : 0)} ${unitLabel(s.unit)}`.trim();
  const point = (id: string) => props.points.find((x) => x.id === id);
  return (
    <Show
      when={props.points.length > 0}
      fallback={
        <section class="card">
          <div class="card-b ovempty">
            <span class="hint">{t("overview.group.noSensors")}</span>
            <A class="btn" href="/assets">
              <Icon name="plus" />
              {t("overview.group.addSensor")}
            </A>
          </div>
        </section>
      }
    >
      <div class="grid g-kpi">
        <For each={summary()}>
          {(s) => (
            <section class="card kpi">
              <div class="lbl">
                <MediumBadge medium={s.medium} /> {t(`medium.${s.medium}`)}
              </div>
              <div class="val">{value(s, s.mean)}</div>
              <div class="hint">
                {t("overview.group.meanOf", {
                  current: s.current,
                  total: s.sensors,
                })}
              </div>
              <Show when={s.current > 1 && s.min && s.max}>
                <dl class="kv ovext">
                  <For
                    each={
                      [
                        ["min", s.min!],
                        ["max", s.max!],
                      ] as const
                    }
                  >
                    {([k, x]) => (
                      <>
                        <dt>{t(`overview.group.${k}`)}</dt>
                        <dd
                          title={`${point(x.pointId)?.name ?? ""} · ${nodeName(point(x.pointId)?.nodeId ?? "")}`}
                        >
                          {value(s, x.value)}{" "}
                          <span class="hint">{point(x.pointId)?.name}</span>
                        </dd>
                      </>
                    )}
                  </For>
                </dl>
              </Show>
            </section>
          )}
        </For>
      </div>
      <section class="card">
        <div class="card-h">
          <h2>{t("overview.conditions")}</h2>
          <span class="meta">{t("overview.conditionsHint")}</span>
        </div>
        <div class="scrollx">
          <table class="tbl">
            <tbody>
              <For
                each={[...props.points].sort(
                  (a, b) =>
                    byConditionOrder(a.medium, b.medium) ||
                    a.name.localeCompare(b.name),
                )}
              >
                {(p) => (
                  <tr
                    class="click"
                    onClick={() =>
                      navigate(`/assets?point=${encodeURIComponent(p.id)}`)
                    }
                  >
                    <td style={{ width: "24px" }}>
                      <MediumBadge medium={p.medium} />
                    </td>
                    <td>
                      {p.name}
                      <div class="hint">{nodeName(p.nodeId)}</div>
                    </td>
                    <td class="r num">
                      <ReadingValue reading={props.readings?.get(p.id)} />
                    </td>
                    <td>
                      <DataChip point={p} reading={props.readings?.get(p.id)} />
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </section>
    </Show>
  );
}
